import React, { useState } from 'react';
import axios from '../axiosInstance';
import { FaDownload, FaInfoCircle, FaTimesCircle, FaCheckCircle } from 'react-icons/fa';
import '../DataCapturer/StepperForm.css';
import {
  NATIONALITY_CATEGORIES, PIP_OPTIONS, PIP_ASSOCIATE_OPTIONS, OCCUPATIONS, SOURCES_OF_INCOME,
  TRANSACTION_SOURCES_OF_FUNDS, PRODUCT_TYPES, DELIVERY_CHANNELS, INSTITUTION_TYPES,
  NATURE_OF_BUSINESS, BO_PIP_OPTIONS
} from './riskOptions';

// Column order must match what POST /customers/bulk reads (server/routes/customers.js).
// Individuals (Client) fill the person columns; every other customer_type
// (Legal Person) fills the business and BO columns.
const TEMPLATE_COLUMNS = [
  'customer_type', 'external_reference', 'kyc_tier', 'product_type', 'delivery_channel',
  'first_name', 'middle_name', 'last_name', 'maiden_name', 'date_of_birth', 'gender',
  'id_type', 'id_number', 'id_expiry_date',
  'nationality', 'nationality_category', 'country_of_residence', 'pip', 'pip_associate',
  'occupation', 'employer', 'source_of_income', 'transaction_source_of_funds', 'monthly_income_band',
  'legal_name', 'trading_name', 'institution', 'registration_number', 'nature_of_business',
  'tax_number', 'industry_sector', 'country_of_incorporation', 'date_of_incorporation',
  'primary_contact_name', 'primary_contact_email', 'primary_contact_phone',
  'business_source_of_funds', 'expected_monthly_volume',
  'bo_first_name', 'bo_last_name', 'bo_pip', 'bo_nationality', 'bo_nationality_category',
  'address_line1', 'address_city', 'address_postal_code', 'address_country'
];

// Allowed values per dropdown column. Either the value shown here (the
// sheet's dropdown text) or its code works.
const labels = (list) => list.map((o) => o.label);
const CODE_LISTS = [
  ['customer_type', ['individual', 'sme', 'agent', 'corporate', 'partner', 'vas_provider', 'internal_wallet']],
  ['kyc_tier', ['lite', 'medium', 'full']],
  ['product_type', labels(PRODUCT_TYPES)],
  ['delivery_channel', labels(DELIVERY_CHANNELS)],
  ['id_type', ['national_id', 'passport', 'drivers_licence', 'refugee_id']],
  ['nationality_category / bo_nationality_category', labels(NATIONALITY_CATEGORIES)],
  ['pip', labels(PIP_OPTIONS)],
  ['pip_associate', labels(PIP_ASSOCIATE_OPTIONS)],
  ['occupation', labels(OCCUPATIONS)],
  ['source_of_income', labels(SOURCES_OF_INCOME)],
  ['transaction_source_of_funds', labels(TRANSACTION_SOURCES_OF_FUNDS)],
  ['monthly_income_band', ['below_5k', '5k_to_15k', 'above_15k']],
  ['institution', labels(INSTITUTION_TYPES)],
  ['nature_of_business', labels(NATURE_OF_BUSINESS)],
  ['bo_pip', labels(BO_PIP_OPTIONS)]
];

const TEMPLATE_EXAMPLES = [
  {
    customer_type: 'individual', external_reference: '0811234567', kyc_tier: 'lite',
    product_type: 'Home loan', delivery_channel: 'Face to Face',
    first_name: 'John', middle_name: 'Michael', last_name: 'Doe', date_of_birth: '15/01/1990',
    gender: 'male', id_type: 'national_id', id_number: '90011500123',
    nationality: 'Namibia', nationality_category: 'Namibian', country_of_residence: 'Namibia',
    pip: 'No', pip_associate: 'No', occupation: 'Employed', employer: 'Ministry of Education',
    source_of_income: 'Salary from formal employment', transaction_source_of_funds: 'Salary',
    monthly_income_band: '5k_to_15k',
    address_line1: '12 Independence Ave', address_city: 'Windhoek',
    address_postal_code: '9000', address_country: 'Namibia'
  },
  {
    customer_type: 'sme', external_reference: 'ACC-10025', kyc_tier: 'medium',
    product_type: 'Transaction account', delivery_channel: 'Online',
    legal_name: 'Example Trading CC', trading_name: 'Example Traders',
    institution: 'CC', registration_number: 'CC/2020/01234',
    nature_of_business: 'Buying and selling of goods', tax_number: '1234567',
    industry_sector: 'Retail', country_of_incorporation: 'Namibia',
    date_of_incorporation: '01/02/2020', primary_contact_name: 'Jane Smith',
    primary_contact_email: 'jane@example.com', primary_contact_phone: '0817654321',
    business_source_of_funds: 'Sales', expected_monthly_volume: '50000',
    bo_first_name: 'Jane', bo_last_name: 'Smith', bo_pip: 'BO-PIP-No',
    bo_nationality: 'Namibia', bo_nationality_category: 'Namibian',
    address_line1: '5 Main Street', address_city: 'Swakopmund',
    address_postal_code: '9000', address_country: 'Namibia'
  }
];

const csvCell = (v) => {
  const s = v === undefined || v === null ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

function BulkCustomerUpload({ closeModal, onUploaded }) {
  const [file, setFile] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleFileChange = (e) => {
    const uploaded = e.target.files[0];
    setResult(null);
    if (!uploaded) { setFile(null); return; }
    const ext = uploaded.name.split('.').pop().toLowerCase();
    if (!['csv', 'xlsx', 'xls'].includes(ext)) {
      setError('Please upload a valid CSV or Excel file.');
      setFile(null);
    } else {
      setError('');
      setFile(uploaded);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) { setError('Please select a file to upload.'); return; }

    const formData = new FormData();
    formData.append('file', file);

    try {
      setLoading(true);
      setError('');
      const res = await axios.post('/customers/bulk', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 10 * 60 * 1000 // each row is risk-scored and screened
      });
      setResult(res.data);
      if (res.data?.created > 0 && onUploaded) onUploaded(res.data);
    } catch (err) {
      setError(err.message || 'Failed to process the file.');
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = () => {
    const lines = [
      TEMPLATE_COLUMNS.join(','),
      ...TEMPLATE_EXAMPLES.map((row) => TEMPLATE_COLUMNS.map((c) => csvCell(row[c])).join(','))
    ];
    const blob = new Blob([lines.join('\r\n') + '\r\n'], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'bulk_customers_template.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  const failedRows = result ? result.results.filter((r) => r.status === 'failed') : [];

  return (
    <div className="modal-content" style={{ maxWidth: '720px' }} onClick={(e) => e.stopPropagation()}>
      <div className="modal-header">
        <h2>Add Bulk Customers</h2>
        <button className="modal-close" onClick={closeModal} disabled={loading}>×</button>
      </div>

      <div className="modal-body">
        <div className="form-section">
          <div className="info-box" style={{ marginBottom: '1.5rem' }}>
            <FaInfoCircle style={{ marginRight: '0.5rem' }} />
            <span>
              Upload a CSV or Excel file with one customer per row (up to 500). Every row needs
              product_type and delivery_channel. Individuals also need first_name, last_name, id_number,
              nationality (or nationality_category), pip, pip_associate, occupation, source_of_income and
              transaction_source_of_funds; legal persons need legal_name, institution and
              nature_of_business. Each customer is risk-scored and screened as it is added.
            </span>
          </div>

          <details style={{ marginBottom: '1rem', fontSize: '0.85rem' }}>
            <summary style={{ cursor: 'pointer', fontWeight: 600 }}>Allowed values for dropdown columns</summary>
            <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.1rem' }}>
              {CODE_LISTS.map(([col, codes]) => (
                <li key={col} style={{ marginBottom: '0.3rem' }}>
                  <strong>{col}</strong>: {codes.join(', ')}
                </li>
              ))}
            </ul>
          </details>

          <button type="button" className="export-button" onClick={downloadTemplate} style={{ marginBottom: '1rem' }}>
            <FaDownload style={{ marginRight: '0.5rem' }} /> Download Template
          </button>

          <form onSubmit={handleSubmit}>
            <div className="step-form-group">
              <label htmlFor="bulkCustomerFile">Select File</label>
              <input
                type="file"
                id="bulkCustomerFile"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileChange}
                disabled={loading}
              />
              {file && (
                <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>
                  Selected: {file.name}
                </p>
              )}
            </div>

            {error && (
              <div className="field-error" style={{ marginTop: '1rem' }}>
                <FaTimesCircle /> {error}
              </div>
            )}

            <button
              type="submit"
              className="export-button"
              disabled={loading || !file}
              style={{ marginTop: '1rem', width: '100%' }}
            >
              {loading ? 'Uploading and screening…' : 'Upload Customers'}
            </button>
          </form>

          {result && (
            <div style={{ marginTop: '1.5rem' }}>
              <div className={`alert-banner ${result.failed ? 'alert-banner--warning' : 'alert-banner--success'}`}>
                <span className="alert-banner__body">
                  <FaCheckCircle /> {result.created} of {result.total} customers added
                  {result.failed ? `, ${result.failed} failed` : ''}.
                </span>
              </div>

              {failedRows.length > 0 && (
                <div className="table-container" style={{ marginTop: '1rem', maxHeight: '260px', overflowY: 'auto' }}>
                  <table className="pips-table">
                    <thead>
                      <tr>
                        <th>Row</th>
                        <th>Name</th>
                        <th>Error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {failedRows.map((r) => (
                        <tr key={r.row}>
                          <td>{r.row}</td>
                          <td>{r.name || '—'}</td>
                          <td>{r.error}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default BulkCustomerUpload;
